import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LockKeyhole } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-4 hero-grain relative" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-white p-8 sm:p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-navy flex items-center justify-center">
            <span className="font-heading font-extrabold text-gold text-xl leading-none">D</span>
          </div>
          <div>
            <p className="font-heading font-extrabold text-navy text-lg leading-tight">DAKSH</p>
            <p className="text-[10px] tracking-[0.18em] uppercase text-slate-500">Admin Portal</p>
          </div>
        </div>
        <h1 className="font-heading text-2xl font-bold text-navy">Sign In</h1>
        <p className="text-sm text-slate-500 mt-1 mb-7">Authorized administrators only.</p>
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="admin-email">Email</Label>
            <Input id="admin-email" type="email" required data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-none border-slate-300 focus-visible:ring-gold" placeholder="admin@dakshddss.com" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password">Password</Label>
            <Input id="admin-password" type="password" required data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-none border-slate-300 focus-visible:ring-gold" placeholder="••••••••" />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-2.5" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-heading font-bold text-xs tracking-widest uppercase px-7 py-4 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <LockKeyhole size={15} />}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <Link to="/" className="block text-center text-xs text-slate-400 hover:text-gold-hover mt-6" data-testid="admin-back-to-site">← Back to website</Link>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
